import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  useAddClaimLine,
  useCancelClaim,
  useClaim,
  useDeleteClaimLine,
  useSubmitClaim,
  useUpdateClaim,
} from "../hooks";
import { ClaimDraftForm } from "../components/ClaimDraftForm";
import { ClaimLineEditor } from "../components/ClaimLineEditor";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { Button } from "#/components/common/Button";
import { useConfirm } from "#/components/common/ConfirmProvider";

export default function ClaimEditPage() {
  const { id } = useParams();
  const claimId = Number(id);
  const navigate = useNavigate();

  const claimQuery = useClaim(claimId);
  const updateClaim = useUpdateClaim(claimId);
  const addLine = useAddClaimLine(claimId);
  const deleteLine = useDeleteClaimLine(claimId);
  const submitClaim = useSubmitClaim();
  const cancelClaim = useCancelClaim();
  const confirm = useConfirm();

  if (!Number.isFinite(claimId) || claimId <= 0) {
    return <ErrorState message="Invalid claim reference." />;
  }

  if (claimQuery.isLoading) {
    return <PageLoader label="Loading claim..." />;
  }

  if (claimQuery.error || !claimQuery.data) {
    return <ErrorState message="Failed to load claim." />;
  }

  const claim = claimQuery.data;
  const editable = claim.status === "DRAFT";

  if (!editable) {
    return (
      <ErrorState message="Only draft claims can be edited. Open the claim details page instead." />
    );
  }

  // If the backend has started returning lineId on documents, enforce per-line.
  // If no document has a lineId yet (backend pending), fall back to "at least one doc total".
  const backendSupportsLineId = claim.documents.some((d) => d.lineId != null);
  const linesWithoutDoc = backendSupportsLineId
    ? claim.lines.filter((l) => !claim.documents.some((d) => d.lineId === l.id))
    : [];
  const canSubmit =
    claim.lines.length > 0 &&
    (backendSupportsLineId ? linesWithoutDoc.length === 0 : claim.documents.length > 0);

  return (
    <div className="space-y-6">
      <Link
        to={`/claims/${claim.id}`}
        className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to claim
      </Link>

      <div className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Edit claim {claim.claimNo}
          </h1>
          <p className="text-sm text-slate-500">
            Add claim lines, attach supporting documents, then submit for approval.
          </p>
        </div>

        <ClaimStatusBadge status={claim.status} />
      </div>

      <ClaimDraftForm
        defaultValues={{
          claimType: claim.claimType,
          purpose: claim.purpose ?? "",
        }}
        loading={updateClaim.isPending}
        onSubmit={async (values) => {
          await updateClaim.mutateAsync({
            claimType: values.claimType,
            purpose: values.purpose,
          });
        }}
      />

      <ClaimLineEditor
        claimId={claim.id}
        lines={claim.lines}
        documents={claim.documents}
        loading={addLine.isPending || deleteLine.isPending}
        editable
        onAdd={(payload) => addLine.mutateAsync(payload)}
        onDelete={(lineId) => deleteLine.mutateAsync(lineId)}
      />

      <div className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:justify-end">
        <Button
          variant="secondary"
          disabled={cancelClaim.isPending}
          onClick={async () => {
            if (!(await confirm("Cancel this draft claim?"))) return;
            await cancelClaim.mutateAsync({
              id: claim.id,
              comments: "Cancelled by employee",
            });
            navigate("/claims");
          }}
        >
          Cancel claim
        </Button>

        <Button
          disabled={!canSubmit || submitClaim.isPending}
          onClick={async () => {
            try {
              await submitClaim.mutateAsync(claim.id);
              // replace: true removes the edit page from history so the
              // Back button never returns to /edit on a submitted claim
              navigate(`/claims/${claim.id}`, { replace: true });
            } catch {
              // error displayed below via submitClaim.isError
            }
          }}
        >
          {submitClaim.isPending ? "Submitting…" : "Submit for approval"}
        </Button>
      </div>

      {claim.lines.length === 0 && (
        <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Add at least one claim line before submitting.
        </p>
      )}
      {claim.lines.length > 0 && linesWithoutDoc.length > 0 && (
        <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Attach a supporting document to each claim line before submitting.
        </p>
      )}
      {submitClaim.isError && (
        <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {(submitClaim.error as { response?: { status?: number } })?.response?.status === 422
            ? "Cannot submit — no approval workflow is configured for claims yet. Contact your administrator to set up a CLAIM workflow in the workflow engine."
            : `Submission failed. Please try again or contact your administrator.`}
        </p>
      )}
    </div>
  );
}